import { useRef, useState, useEffect, createContext, useContext } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { 
  Video, 
  Monitor, 
  Brain, 
  Music, 
  Film, 
  Camera, 
  PenTool, 
  FileText, 
  Sparkles,
  ArrowRight,
  Mail,
  ChevronDown,
  Send,
  Loader2,
  CheckCircle,
  Youtube,
  Instagram,
  Facebook,
  Settings,
  DollarSign,
  ShoppingCart,
  X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Site Settings Context
const SiteSettingsContext = createContext(null);

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true }
};

// Icon mapping for services
const iconMap = {
  "Video Production": Video,
  "Web Design": Monitor,
  "AI Bot Solutions": Brain,
  "YouTube Content": Youtube,
  "Instagram Content": Instagram,
  "Facebook Content": Facebook,
  "Audio & Jingles": Music,
  "Video Editing": Film,
  "OBS Consultation": Settings,
  "Photography": Camera,
  "Logo Design": PenTool,
  "Scripting": FileText,
  "Commercial Ads": Sparkles
};

// Default span mapping
const spanMap = {
  "Video Production": "md:col-span-2 md:row-span-2",
  "YouTube Content": "md:col-span-2 row-span-1",
  "Audio & Jingles": "md:col-span-2 row-span-1"
};

const defaultServices = [
  {
    title: "Video Production",
    description: "Professional videography for commercials, events, and brand content",
    icon: Video,
    image: "https://images.unsplash.com/photo-1634812932028-3baa37d90b52?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    span: "md:col-span-2 md:row-span-2"
  },
  {
    title: "Web Design",
    description: "Custom websites for sales, appointments, and information",
    icon: Monitor,
    image: "https://images.unsplash.com/photo-1553525302-38381e3e0c9f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    span: "col-span-1 row-span-1"
  },
  {
    title: "AI Bot Solutions",
    description: "Intelligent chatbots and automation implementation",
    icon: Brain,
    image: "https://images.unsplash.com/photo-1701505708176-63194ee8f0e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    span: "col-span-1 row-span-1"
  },
  {
    title: "YouTube Content",
    description: "Engaging video content optimized for YouTube growth",
    icon: Youtube,
    image: "https://images.unsplash.com/photo-1611162616475-46b635cb6868?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    span: "md:col-span-2 row-span-1"
  },
  {
    title: "Instagram Content",
    description: "Eye-catching reels, stories, and posts for Instagram",
    icon: Instagram,
    image: "https://images.unsplash.com/photo-1611262588024-d12430b98920?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    span: "col-span-1 row-span-1"
  },
  {
    title: "Facebook Content",
    description: "Strategic content creation for Facebook engagement",
    icon: Facebook,
    image: "https://images.unsplash.com/photo-1579869847514-7c1a19d2d2ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    span: "col-span-1 row-span-1"
  },
  {
    title: "Audio & Jingles",
    description: "Custom jingle creation and audio production",
    icon: Music,
    image: "https://images.unsplash.com/photo-1591960028610-17925389af25?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    span: "md:col-span-2 row-span-1"
  },
  {
    title: "Video Editing",
    description: "Professional post-production and editing services",
    icon: Film,
    image: "https://images.unsplash.com/photo-1536724609414-5f000e9a2745?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    span: "col-span-1 row-span-1"
  },
  {
    title: "OBS Consultation",
    description: "Expert setup and optimization for streaming with OBS",
    icon: Settings,
    image: "https://images.unsplash.com/photo-1587620962725-abab7fe55159?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    span: "col-span-1 row-span-1"
  },
  {
    title: "Photography",
    description: "High-quality photography for products and events",
    icon: Camera,
    image: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    span: "col-span-1 row-span-1"
  },
  {
    title: "Logo Design",
    description: "Company logos and brand imagery that stand out",
    icon: PenTool,
    image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    span: "col-span-1 row-span-1"
  },
  {
    title: "Scripting",
    description: "Compelling scripts for ads, videos, and presentations",
    icon: FileText,
    image: "https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    span: "col-span-1 row-span-1"
  },
  {
    title: "Commercial Ads",
    description: "Full-service advertisement production",
    icon: Sparkles,
    image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    span: "col-span-1 row-span-1"
  }
];

const defaultPortfolioImages = [
  "https://images.unsplash.com/photo-1598814941971-d5d4f0614229?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1739470413072-5ec7c3766d54?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1701505708176-63194ee8f0e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1591960028610-17925389af25?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1536724609414-5f000e9a2745?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1626785774573-4b799315345d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
];

const defaultHeroImage = "https://images.unsplash.com/photo-1739470413072-5ec7c3766d54?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const defaultAboutImage = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const defaultEmail = "streamcaster.dmca+business@gmail.com";

// Navigation Component with Mobile Menu
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 glass"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 sm:gap-3" data-testid="nav-logo">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#0A3F2F] flex items-center justify-center">
              <span className="text-white font-bold text-base sm:text-lg">S</span>
            </div>
            <span className="font-['Syne'] font-bold text-lg sm:text-xl text-[#0A3F2F]">Streamcaster</span>
          </a>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('services')} className="nav-link text-[#0A3F2F] font-medium" data-testid="nav-services">Services</button>
            <button onClick={() => scrollToSection('portfolio')} className="nav-link text-[#0A3F2F] font-medium" data-testid="nav-portfolio">Portfolio</button>
            <button onClick={() => scrollToSection('about')} className="nav-link text-[#0A3F2F] font-medium" data-testid="nav-about">About</button>
            <button 
              onClick={() => scrollToSection('contact')} 
              className="bg-[#0A3F2F] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#0d5139] transition-colors duration-300"
              data-testid="nav-contact-btn"
            >
              Get in Touch
            </button>
          </div>

          {/* Mobile Hamburger Button */}
          <button 
            className="md:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            <motion.span 
              className="block w-6 h-0.5 bg-[#0A3F2F]"
              animate={mobileMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.2 }}
            />
            <motion.span 
              className="block w-6 h-0.5 bg-[#0A3F2F]"
              animate={mobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
              transition={{ duration: 0.2 }}
            />
            <motion.span 
              className="block w-6 h-0.5 bg-[#0A3F2F]"
              animate={mobileMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.2 }}
            />
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <motion.div
        className="fixed inset-0 z-40 bg-[#F5F5F0] md:hidden"
        initial={{ opacity: 0, x: "100%" }}
        animate={mobileMenuOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: "100%" }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ pointerEvents: mobileMenuOpen ? 'auto' : 'none' }}
      >
        <div className="flex flex-col items-center justify-center h-full gap-8 pt-16">
          <button 
            onClick={() => scrollToSection('services')} 
            className="text-[#0A3F2F] font-['Syne'] text-2xl font-bold hover:text-[#D4FF33] transition-colors"
            data-testid="mobile-nav-services"
          >
            Services
          </button>
          <button 
            onClick={() => scrollToSection('portfolio')} 
            className="text-[#0A3F2F] font-['Syne'] text-2xl font-bold hover:text-[#D4FF33] transition-colors"
            data-testid="mobile-nav-portfolio"
          >
            Portfolio
          </button>
          <button 
            onClick={() => scrollToSection('about')} 
            className="text-[#0A3F2F] font-['Syne'] text-2xl font-bold hover:text-[#D4FF33] transition-colors"
            data-testid="mobile-nav-about"
          >
            About
          </button>
          <button 
            onClick={() => scrollToSection('contact')} 
            className="bg-[#0A3F2F] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#0d5139] transition-colors"
            data-testid="mobile-nav-contact"
          >
            Get in Touch
          </button>
        </div>
      </motion.div>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const settings = useContext(SiteSettingsContext);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Background Image with Parallax */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y }}
      >
        <img 
          src={settings?.hero_image || defaultHeroImage} 
          alt="Abstract background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#F5F5F0]/80" />
      </motion.div>

      {/* Content */}
      <motion.div 
        className="relative z-10 text-center px-4 sm:px-6 md:px-12 w-full flex flex-col items-center justify-center"
        style={{ opacity }}
      >
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="inline-block bg-[#D4FF33] text-[#0A3F2F] px-4 py-1.5 rounded-full text-sm font-semibold mb-8">
            Creative Studio
          </span>
        </motion.div>

        <motion.h1 
          className="font-['Syne'] text-[1.5rem] sm:text-4xl md:text-6xl lg:text-8xl font-extrabold text-[#0A3F2F] leading-tight mb-6"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          data-testid="hero-title"
        >
          <span className="block text-center">STREAMCASTER</span>
          <span className="block text-center hero-gradient-text">STUDIOS</span>
        </motion.h1>

        <motion.p 
          className="text-lg md:text-xl text-[#4A5F55] max-w-2xl mx-auto mb-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          data-testid="hero-description"
        >
          We craft compelling visual stories, build stunning websites, and deliver creative solutions that make your brand unforgettable.
        </motion.p>

        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <button 
            onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#0A3F2F] text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 hover:bg-[#0d5139] transition-colors duration-300 btn-pulse"
            data-testid="hero-explore-btn"
          >
            Explore Services
            <ArrowRight size={20} />
          </button>
          <button 
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="border-2 border-[#0A3F2F] text-[#0A3F2F] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#0A3F2F] hover:text-white transition-colors duration-300"
            data-testid="hero-contact-btn"
          >
            Start a Project
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <ChevronDown size={32} className="text-[#0A3F2F]" />
      </motion.div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const settings = useContext(SiteSettingsContext);
  const services = settings?.services || defaultServices;
  const [selectedService, setSelectedService] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    // Fetch PayPal client ID
    axios.get(`${API}/paypal/client-id`)
      .then(res => setPaypalClientId(res.data.client_id))
      .catch(err => console.error("Failed to get PayPal client ID"));
  }, []);

  const formatPrice = (service) => {
    if (service.price_type === "fixed" && service.price) {
      return `$${service.price.toFixed(0)}`;
    } else if (service.price_type === "starting" && service.price) {
      return `From $${service.price.toFixed(0)}`;
    }
    return "Get Quote";
  };

  const canPurchase = (service) => {
    return service.price_type === "fixed" && service.price > 0;
  };

  const handleBuyClick = (service) => {
    if (canPurchase(service)) {
      setSelectedService(service);
      setPaymentSuccess(false);
    } else {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const createOrder = async () => {
    try {
      const response = await axios.post(`${API}/paypal/create-order`, {
        service_title: selectedService.title,
        amount: selectedService.price,
        description: selectedService.description
      });
      return response.data.order_id;
    } catch (error) {
      toast.error("Failed to create order");
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      await axios.post(`${API}/paypal/capture-order/${data.orderID}`);
      setPaymentSuccess(true);
      toast.success("Payment successful! We'll be in touch shortly.");
    } catch (error) {
      toast.error("Payment failed. Please try again.");
    }
  };

  return (
    <section id="services" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="mb-16"
          {...fadeUp}
        >
          <span className="text-[#D4FF33] bg-[#0A3F2F] px-3 py-1 rounded-full text-sm font-semibold">What We Do</span>
          <h2 className="font-['Syne'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0A3F2F] mt-4" data-testid="services-title">
            Our Services
          </h2>
          <p className="text-[#4A5F55] text-lg mt-4 max-w-xl">
            From concept to creation, we bring your vision to life with our comprehensive range of creative services.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          {services.map((service, index) => {
            const IconComponent = service.icon || iconMap[service.title] || Sparkles;
            return (
              <motion.div
                key={service.title}
                className={`service-card min-h-[200px] md:min-h-[250px] ${service.span || spanMap[service.title] || 'col-span-1 row-span-1'} group cursor-pointer`}
                variants={fadeUp}
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
                data-testid={`service-card-${index}`}
                onClick={() => handleBuyClick(service)}
              >
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="transition-transform duration-500 group-hover:scale-110"
                />
                <div className="service-card-content">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-[#D4FF33] flex items-center justify-center">
                      <IconComponent size={20} className="text-[#0A3F2F]" />
                    </div>
                    <h3 className="font-['Syne'] text-xl font-bold text-white">{service.title}</h3>
                  </div>
                  <p className="text-white/80 text-sm mb-3">{service.description}</p>
                  
                  {/* Price Display */}
                  <div className="flex items-center justify-between mt-auto pt-2 border-t border-white/20">
                    <span className="text-[#D4FF33] font-bold text-lg" data-testid={`service-price-${index}`}>
                      {formatPrice(service)}
                    </span>
                    <button 
                      className="flex items-center gap-1 bg-[#D4FF33] text-[#0A3F2F] px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-white transition-colors"
                      onClick={(e) => { e.stopPropagation(); handleBuyClick(service); }}
                      data-testid={`service-buy-${index}`}
                    >
                      {canPurchase(service) ? (
                        <>
                          <ShoppingCart size={14} />
                          Buy Now
                        </>
                      ) : (
                        <>
                          <Mail size={14} />
                          Contact
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* PayPal Payment Modal */}
      {selectedService && paypalClientId && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setSelectedService(null)}
        >
          <motion.div 
            className="bg-white rounded-2xl max-w-md w-full p-6 relative"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="absolute top-4 right-4 text-[#4A5F55] hover:text-[#0A3F2F]"
              onClick={() => setSelectedService(null)}
              data-testid="close-payment-modal"
            >
              <X size={24} />
            </button>

            {paymentSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="font-['Syne'] text-2xl font-bold text-[#0A3F2F] mb-2">Payment Successful!</h3>
                <p className="text-[#4A5F55]">Thank you for your purchase. We'll contact you shortly to get started on your {selectedService.title} project.</p>
                <button 
                  className="mt-6 bg-[#0A3F2F] text-white px-6 py-3 rounded-full font-medium"
                  onClick={() => setSelectedService(null)}
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-['Syne'] text-2xl font-bold text-[#0A3F2F] mb-2">{selectedService.title}</h3>
                <p className="text-[#4A5F55] text-sm mb-4">{selectedService.description}</p>
                
                <div className="bg-[#F5F5F0] rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-[#4A5F55]">Total</span>
                    <span className="font-['Syne'] text-3xl font-bold text-[#0A3F2F]">${selectedService.price?.toFixed(2)}</span>
                  </div>
                </div>

                <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "USD" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "pill" }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={() => toast.error("Payment error. Please try again.")}
                  />
                </PayPalScriptProvider>

                <p className="text-xs text-center text-[#4A5F55] mt-4">
                  Secure payment powered by PayPal
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  const settings = useContext(SiteSettingsContext);
  const portfolioImages = settings?.portfolio_images || defaultPortfolioImages;

  return (
    <section id="portfolio" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#EBEBE3]" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="mb-16"
          {...fadeUp}
        >
          <span className="text-[#0A3F2F] bg-[#D4FF33] px-3 py-1 rounded-full text-sm font-semibold">Our Work</span>
          <h2 className="font-['Syne'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0A3F2F] mt-4" data-testid="portfolio-title">
            Portfolio
          </h2>
          <p className="text-[#4A5F55] text-lg mt-4 max-w-xl">
            A glimpse into our creative journey. Each project tells a unique story.
          </p>
        </motion.div>

        {/* Masonry-style Grid */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          {portfolioImages.map((image, index) => (
            <motion.div
              key={index}
              className={`portfolio-item ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''} ${index === 3 ? 'md:col-span-2' : ''}`}
              variants={fadeUp}
              data-testid={`portfolio-item-${index}`}
            >
              <img 
                src={image} 
                alt={`Portfolio work ${index + 1}`}
                className={`w-full ${index === 0 ? 'h-[300px] md:h-full' : 'h-[200px] md:h-[250px]'}`}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const settings = useContext(SiteSettingsContext);

  return (
    <section id="about" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A3F2F]" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <motion.div {...fadeUp}>
            <span className="text-[#0A3F2F] bg-[#D4FF33] px-3 py-1 rounded-full text-sm font-semibold">About Us</span>
            <h2 className="font-['Syne'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mt-4 mb-6" data-testid="about-title">
              We Are Streamcaster
            </h2>
            <p className="text-white/80 text-lg mb-6 leading-relaxed">
              Streamcaster Studios is a full-service creative agency passionate about bringing ideas to life. We blend artistry with technology to create impactful visual content, stunning websites, and innovative solutions.
            </p>
            <p className="text-white/70 text-base mb-8 leading-relaxed">
              From logo design to video production, from AI implementation to commercial advertisements — we're your one-stop creative partner. Our team combines years of experience with cutting-edge tools to deliver results that exceed expectations.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="bg-[#D4FF33] px-6 py-4 rounded-xl">
                <div className="font-['Syne'] text-3xl font-bold text-[#0A3F2F]">13+</div>
                <div className="text-[#0A3F2F] text-sm font-medium">Services</div>
              </div>
              <div className="bg-white/10 px-6 py-4 rounded-xl">
                <div className="font-['Syne'] text-3xl font-bold text-white">100%</div>
                <div className="text-white/70 text-sm font-medium">Dedication</div>
              </div>
              <div className="bg-white/10 px-6 py-4 rounded-xl">
                <div className="font-['Syne'] text-3xl font-bold text-white">24/7</div>
                <div className="text-white/70 text-sm font-medium">Support</div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src={settings?.about_image || defaultAboutImage} 
                alt="Streamcaster team at work"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#D4FF33] p-6 rounded-xl shadow-lg">
              <Sparkles size={32} className="text-[#0A3F2F]" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact Section with Form
const ContactSection = () => {
  const settings = useContext(SiteSettingsContext);
  const contactEmail = settings?.contact_email || defaultEmail;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const serviceOptions = [
    "Video Production",
    "Web Design",
    "AI Bot Solutions",
    "YouTube Content",
    "Instagram Content",
    "Facebook Content",
    "Audio & Jingles",
    "Video Editing",
    "OBS Consultation",
    "Photography",
    "Logo Design",
    "Scripting",
    "Commercial Ads",
    "Other"
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", service: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]" data-testid="contact-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          {/* Left - Info */}
          <motion.div {...fadeUp}>
            <span className="text-[#D4FF33] bg-[#0A3F2F] px-3 py-1 rounded-full text-sm font-semibold">Contact</span>
            <h2 className="font-['Syne'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0A3F2F] mt-4 mb-6" data-testid="contact-title">
              Let's Create
            </h2>
            <p className="text-[#4A5F55] text-lg mb-8">
              Ready to bring your vision to life? Fill out the form and let's start something amazing together.
            </p>
            
            <div className="space-y-4">
              <a 
                href={`mailto:${contactEmail}`}
                className="email-link inline-flex items-center gap-3 text-[#0A3F2F] font-semibold hover:text-[#0d5139] transition-colors duration-300"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 rounded-full bg-[#D4FF33] flex items-center justify-center">
                  <Mail size={20} className="text-[#0A3F2F]" />
                </div>
                <span className="text-base">{contactEmail}</span>
              </a>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {isSubmitted ? (
              <div className="bg-[#EBEBE3] rounded-2xl p-8 text-center" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full bg-[#D4FF33] flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-[#0A3F2F]" />
                </div>
                <h3 className="font-['Syne'] text-2xl font-bold text-[#0A3F2F] mb-2">Message Sent!</h3>
                <p className="text-[#4A5F55] mb-6">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-[#0A3F2F] font-semibold underline hover:no-underline"
                  data-testid="send-another-btn"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-[#EBEBE3] rounded-2xl p-6 md:p-8 space-y-5" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-[#0A3F2F] font-medium mb-2">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full px-4 py-3 rounded-xl bg-[#F5F5F0] border border-[#D1D1C7] text-[#0A3F2F] placeholder-[#4A5F55]/50 focus:outline-none focus:ring-2 focus:ring-[#0A3F2F] focus:border-transparent transition-shadow"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-[#0A3F2F] font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-xl bg-[#F5F5F0] border border-[#D1D1C7] text-[#0A3F2F] placeholder-[#4A5F55]/50 focus:outline-none focus:ring-2 focus:ring-[#0A3F2F] focus:border-transparent transition-shadow"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-[#0A3F2F] font-medium mb-2">Service Interested In</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-[#F5F5F0] border border-[#D1D1C7] text-[#0A3F2F] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F] focus:border-transparent transition-shadow"
                    data-testid="contact-service-select"
                  >
                    <option value="">Select a service</option>
                    {serviceOptions.map((service) => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-[#0A3F2F] font-medium mb-2">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    minLength={10}
                    placeholder="Tell us about your project..."
                    className="w-full px-4 py-3 rounded-xl bg-[#F5F5F0] border border-[#D1D1C7] text-[#0A3F2F] placeholder-[#4A5F55]/50 focus:outline-none focus:ring-2 focus:ring-[#0A3F2F] focus:border-transparent transition-shadow resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0A3F2F] text-white px-6 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-3 hover:bg-[#0d5139] transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={20} />
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const settings = useContext(SiteSettingsContext);
  const contactEmail = settings?.contact_email || defaultEmail;

  return (
    <footer className="py-8 px-6 md:px-12 bg-[#0A3F2F]" data-testid="footer">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#D4FF33] flex items-center justify-center">
            <span className="text-[#0A3F2F] font-bold text-sm">S</span>
          </div>
          <span className="font-['Syne'] font-bold text-white">Streamcaster Studios</span>
        </div>
        <a href={`mailto:${contactEmail}`} className="text-white/60 text-sm hover:text-white transition-colors">
          {contactEmail}
        </a>
        <p className="text-white/60 text-sm">
          © {new Date().getFullYear()} Streamcaster Studios. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

// Main Page Component
export default function HomePage() {
  const [siteSettings, setSiteSettings] = useState({
    contact_email: defaultEmail,
    hero_image: defaultHeroImage,
    about_image: defaultAboutImage,
    services: defaultServices,
    portfolio_images: defaultPortfolioImages
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSiteSettings({
          contact_email: res.data.contact_email || defaultEmail,
          hero_image: res.data.hero_image || defaultHeroImage,
          about_image: res.data.about_image || defaultAboutImage,
          services: res.data.services?.length > 0 ? res.data.services.map(s => ({
            ...s,
            icon: iconMap[s.title] || Sparkles,
            span: spanMap[s.title] || "col-span-1 row-span-1"
          })) : defaultServices,
          portfolio_images: res.data.portfolio_images?.length > 0 ? res.data.portfolio_images : defaultPortfolioImages
        });
      } catch (error) {
        console.log("Using default settings");
      }
    };
    fetchSettings();
  }, []);

  return (
    <SiteSettingsContext.Provider value={siteSettings}>
      <main className="overflow-x-hidden">
        <Navigation />
        <HeroSection />
        <ServicesSection />
        <PortfolioSection />
        <AboutSection />
        <ContactSection />
        <Footer />
      </main>
    </SiteSettingsContext.Provider>
  );
}
